// src/components/common/Button.jsx
import React from 'react';

const Button = ({
    children,
    variant = 'primary',
    size = 'md',
    type = 'button',
    onClick,
    disabled = false,
    className = '',
    icon = null,
    loading = false,
    ...props
}) => {
    const baseStyles = 'inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2';

    const variants = {
        // Standard solid colors
        primary: 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800 shadow-sm hover:shadow-md focus:ring-primary-500',
        secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 shadow-sm focus:ring-gray-300',
        success: 'bg-success-600 text-white hover:bg-success-700 active:bg-success-800 shadow-sm hover:shadow-md focus:ring-success-500',
        danger: 'bg-danger-600 text-white hover:bg-danger-700 active:bg-danger-800 shadow-sm hover:shadow-md focus:ring-danger-500',
        warning: 'bg-warning-500 text-white hover:bg-warning-600 active:bg-warning-700 shadow-sm hover:shadow-md focus:ring-warning-500',
        info: 'bg-accent-600 text-white hover:bg-accent-700 active:bg-accent-800 shadow-sm hover:shadow-md focus:ring-accent-500',
        ghost: 'text-gray-700 hover:bg-gray-100 focus:ring-gray-300',
        link: 'text-primary-600 hover:text-primary-700 underline-offset-4 hover:underline focus:ring-primary-500',
        
        // Modern gradient variants for actions
        'gradient-primary': 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg hover:shadow-xl hover:from-purple-700 hover:to-indigo-700 transform hover:scale-105 focus:ring-purple-500',
        'gradient-view': 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow hover:shadow-lg transform hover:scale-105 focus:ring-purple-500',
        'gradient-edit': 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow hover:shadow-lg transform hover:scale-105 focus:ring-blue-500',
        'gradient-success': 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow hover:shadow-lg transform hover:scale-105 focus:ring-green-500',
        'gradient-warning': 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow hover:shadow-lg transform hover:scale-105 focus:ring-yellow-500',
        'gradient-danger': 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow hover:shadow-lg transform hover:scale-105 focus:ring-red-500',
        'gradient-create': 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg hover:shadow-xl hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 focus:ring-green-500',
    };

    const sizes = {
        xs: 'px-2.5 py-1 text-xs',
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-sm',
        lg: 'px-6 py-3 text-base',
        xl: 'px-8 py-4 text-lg',
    };

    const variantClass = variants[variant] || variants.primary;
    const sizeClass = sizes[size] || sizes.md;

    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled || loading}
            className={`${baseStyles} ${variantClass} ${sizeClass} ${className}`}
            {...props}
        >
            {loading ? (
                <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Loading...</span>
                </>
            ) : (
                <>
                    {icon && <span className="flex-shrink-0">{icon}</span>}
                    {children}
                </>
            )}
        </button>
    );
};

export default Button;
